import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import contactService from "../../api/contactService";
import profileService from "../../api/profileService";
import { setUser, getUser } from "../../utils/auth";

// Action flags
export const ACTION_FLAGS = {
  SUBMIT_CONTACT_FORM: "profile/submitForm",
};

// Async thunk for submitting contact form
export const submitContactForm = createAsyncThunk(
  ACTION_FLAGS.SUBMIT_CONTACT_FORM,
  async (formData, { rejectWithValue }) => {
    try {
      const response = await contactService.createSupport(formData);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to send message";
      return rejectWithValue(message);
    }
  },
);

// async function getGoodListRequest(params) {
//   return instance
//     .get(`${API_ENDPOINTS.goods.list}`, { params })
//     .then((items) => items.data)
//     .catch((error) => {
//       throw error?.response;
//     });
// }

export const getViewProfile = createAsyncThunk(
  "profile/getViewProfile",
  async () => {
    try {
      const response = await profileService.viewProfile();
      if (response) {
        return {
          profileItem: response?.data,
          actionFlag: "VIEWPROFILE_SCS",
          success: "",
          error: "",
        };
      }
    } catch (error) {
      return {
        profileItem: {},
        actionFlag: "VIEWPROFILE_ERR",
        success: "",
        error: error,
      };
    }
  },
);

export const getRoles = createAsyncThunk("profile/getRoles", async () => {
  try {
    const response = await profileService.listRoles();
    if (response) {
      return {
        roleItems: response?.data,
        actionFlag: "ROLE_SCS",
        success: "",
        error: "",
      };
    }
  } catch (error) {
    return {
      roleItems: {},
      actionFlag: "ROLE_ERR",
      success: "",
      error: error,
    };
  }
});

export const getExperience = createAsyncThunk(
  "profile/getExperience",
  async () => {
    try {
      const response = await profileService.listExperience();
      if (response) {
        return {
          experienceItems: response?.data,
          actionFlag: "EXPERIENCE_SCS",
          success: "",
          error: "",
        };
      }
    } catch (error) {
      return {
        experienceItems: {},
        actionFlag: "EXPERIENCE_ERR",
        success: "",
        error: error,
      };
    }
  },
);

export const getAllStates = createAsyncThunk(
  "profile/getAllStates",
  async () => {
    try {
      const response = await profileService.listState();
      if (response) {
        return {
          stateItems: response?.data,
          actionFlag: "STATES_SCS",
          success: "",
          error: "",
        };
      }
    } catch (error) {
      return {
        stateItems: {},
        actionFlag: "STATES_ERR",
        success: "",
        error: error,
      };
    }
  },
);

export const getAllCities = createAsyncThunk(
  "profile/getAllCities",
  async (params) => {
    try {
      const response = await profileService.listCity(params);
      if (response) {
        return {
          params,
          cityItems: response?.data,
          actionFlag: "CITIES_SCS",
          success: "",
          error: "",
        };
      }
    } catch (error) {
      return {
        params,
        cityItems: {},
        actionFlag: "CITIES_ERR",
        success: "",
        error: error,
      };
    }
  },
);

export const getLocationChoice = createAsyncThunk(
  "profile/getLocationChoice",
  async () => {
    try {
      const response = await profileService.listLocationChoice();

      if (response) {
        return {
          locationChoiceItems: response?.data,
          actionFlag: "LOCATION_SCS",
          success: "",
          error: "",
        };
      }
    } catch (error) {
      return {
        locationChoiceItems: {},
        actionFlag: "LOCATION_ERR",
        success: "",
        error: error,
      };
    }
  },
);

export const updateProfile = createAsyncThunk(
  "profile/updateProfile  ",
  async (payload) => {
    try {
      const response = await profileService.updateProfile(payload);

      if (response) {
        return {
          profileItem: response?.data,
          actionFlag: "PROFILE_UPDT_SCS",
          success: "",
          error: "",
        };
      }
    } catch (error) {
      return {
        profileItem: {},
        actionFlag: "PROFILE_UPDT_ERR",
        success: "",
        error: error,
      };
    }
  },
);

export const uploadCrtDocument = createAsyncThunk(
  "/profile/uploadCrtDocument",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await profileService.uploadCrtDocument(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Initial state
const initialState = {
  profileItem: {},
  experienceItems: {},
  uploadCrtDocumentItems: {},
  cityItems: {},
  stateItems: {},
  roleItems: {},
  actionFlag: "",
  locationChoiceItems: {},
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  successMessage: "",
};

const contactSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    // Reset state
    resetContactState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.successMessage = "";
      state.actionFlag = "";
    },
    // Clear messages
    clearMessages: (state) => {
      state.successMessage = "";
      state.errorMessage = "";
      state.actionFlag = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitContactForm.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(submitContactForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.errorMessage = "";
      })
      .addCase(submitContactForm.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(getViewProfile.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(getViewProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.profileItem = action.payload.profileItem || {};
        state.errorMessage = "";
        state.actionFlag = action.payload.actionFlag;

        // Sync with auth.user in localStorage
        if (action.payload.profileItem?.data) {
          const currentUser = getUser() || {};
          setUser({ ...currentUser, ...action.payload.profileItem.data });
        }
      })
      .addCase(getViewProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(getRoles.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.roleItems = action.payload.roleItems || {};
        state.errorMessage = "";
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(getExperience.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(getExperience.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.experienceItems = action.payload.experienceItems || {};
        state.errorMessage = "";
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(getExperience.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.profileItem = action.payload.profileItem || {};
        state.errorMessage = "";
        state.actionFlag = action.payload.actionFlag;

        // Sync with auth.user in localStorage
        if (action.payload.profileItem?.data) {
          const currentUser = getUser() || {};
          setUser({ ...currentUser, ...action.payload.profileItem.data });
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(getLocationChoice.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(getLocationChoice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.locationChoiceItems = action.payload.locationChoiceItems || {};
        state.errorMessage = "";
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(getLocationChoice.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(getAllStates.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(getAllStates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.stateItems = action.payload.stateItems || {};
        state.errorMessage = "";
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(getAllStates.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(getAllCities.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(getAllCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.successMessage =
          action.payload.message || "Message sent successfully!";
        state.cityItems = action.payload.cityItems || {};
        state.errorMessage = "";
        state.actionFlag = action.payload.actionFlag;
      })
      .addCase(getAllCities.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.successMessage = "";
      })
      .addCase(uploadCrtDocument.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(uploadCrtDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.uploadCrtDocumentItems = action.payload || "";
        state.errorMessage = "";
      })
      .addCase(uploadCrtDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

export const { resetContactState, clearMessages } = contactSlice.actions;
export default contactSlice.reducer;
